import React, { useMemo, useState } from 'react';
import {
    Box,
    Chip,
    CircularProgress,
    Grid2,
    IconButton,
    Table,
    TableContainer,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import BlockWithTitle from '../../../components/atom/BlockWithTitle';
import ShowMoreToggle from './ShowMoreToggle';
import { formatDateTimeInVenueTz } from '../../../helpers/formatDateTime';
import { formatCentsToUsd } from '../../../helpers/formatCentsToUsd';

const IGNORED_DIFF_KEYS = ['createdAt', 'updatedAt', 'deletedAt', 'booking_id'];
const CENTS_KEYS = new Set([
    'price',
    'amount',
    'discount_amount',
    'commission_amount',
    'commissionDeltaCents',
]);
const HIDDEN_PAYLOAD_KEYS = new Set(['bookingId', 'venueId', 'booking_id', 'venue_id']);
const TICKET_TYPE_KEYS = new Set(['ticket_type_id', 'ticketTypeId']);
const TICKET_OPTION_KEYS = new Set(['ticket_option_id', 'ticketOptionId']);
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(T|\s)\d{2}:\d{2}/;
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
const MAX_STRING_LENGTH = 100;
const MAX_COMPACT_ITEMS = 3;

function humaniseKey(key) {
    return String(key)
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

function show(value) {
    if (value == null || value === '') return '<empty>';
    if (Array.isArray(value)) {
        if (value.length === 0) return '[]';
        const items = value.slice(0, MAX_COMPACT_ITEMS).map(show).join(', ');
        return value.length > MAX_COMPACT_ITEMS
            ? `[${items}, …${value.length - MAX_COMPACT_ITEMS} more]`
            : `[${items}]`;
    }
    if (typeof value === 'object') {
        const entries = Object.entries(value);
        if (entries.length === 0) return '{}';
        const shown = entries
            .slice(0, MAX_COMPACT_ITEMS)
            .map(([k, v]) => `${k}: ${show(v)}`)
            .join(', ');
        return entries.length > MAX_COMPACT_ITEMS
            ? `{${shown}, …${entries.length - MAX_COMPACT_ITEMS} more}`
            : `{${shown}}`;
    }
    const str = String(value);
    return str.length > MAX_STRING_LENGTH ? `${str.slice(0, MAX_STRING_LENGTH)}…` : str;
}

function EmptyValue() {
    return (
        <Typography component="span" variant="body2" sx={{ fontStyle: 'italic', opacity: 0.6 }}>
            empty
        </Typography>
    );
}

// Renders a single leaf value. Turns ticket_type_id / ticket_option_id into
// named chips, cents fields into dollars, ISO dates into venue-local time.
function Scalar({ keyName, value, ticketTypes, ticketOptions }) {
    if (value == null || value === '') return <EmptyValue />;

    if (TICKET_TYPE_KEYS.has(keyName)) {
        const match = ticketTypes?.find((t) => t.id === Number(value));
        return (
            <Chip
                size="small"
                variant="outlined"
                color="info"
                label={match?.name || `Ticket type #${value}`}
            />
        );
    }

    if (TICKET_OPTION_KEYS.has(keyName)) {
        const match = ticketOptions?.find((o) => o.id === Number(value));
        return (
            <Chip
                size="small"
                variant="outlined"
                color="secondary"
                label={match?.name || `Ticket option #${value}`}
            />
        );
    }

    if (typeof value === 'number' && CENTS_KEYS.has(keyName)) {
        return <>{formatCentsToUsd(value)}</>;
    }

    if (typeof value === 'string' && (ISO_DATE_RE.test(value) || DATE_ONLY_RE.test(value))) {
        const formatted = formatDateTimeInVenueTz(value);
        if (formatted) return <>{formatted}</>;
    }

    return <>{show(value)}</>;
}

// Walks any payload and produces { path: string[], value } entries, where
// path's last segment is the leaf label. Recurses into objects and arrays so
// every leaf gets its own row.
function renderPayload(data, ctx, pathPrefix = []) {
    if (!data || typeof data !== 'object') return [];
    const lines = [];

    for (const [key, value] of Object.entries(data)) {
        if (HIDDEN_PAYLOAD_KEYS.has(key)) continue;
        if (value === undefined || value === null || value === '') continue;

        const path = [...pathPrefix, humaniseKey(key)];

        if (Array.isArray(value)) {
            if (value.length === 0) continue;
            const allPrimitive = value.every((v) => v === null || typeof v !== 'object');
            if (allPrimitive) {
                lines.push({
                    path,
                    value: (
                        <Box
                            component="span"
                            sx={{ display: 'inline-flex', gap: 0.5, flexWrap: 'wrap' }}
                        >
                            {value.map((v, i) => (
                                <React.Fragment key={i}>
                                    {i > 0 && <>, </>}
                                    <Scalar keyName={key} value={v} {...ctx} />
                                </React.Fragment>
                            ))}
                        </Box>
                    ),
                });
                continue;
            }
            value.forEach((item, i) => {
                lines.push(...renderPayload(item, ctx, [...path, String(i + 1)]));
            });
            continue;
        }

        if (typeof value === 'object') {
            lines.push(...renderPayload(value, ctx, path));
            continue;
        }

        lines.push({
            path,
            value: <Scalar keyName={key} value={value} {...ctx} />,
        });
    }

    return lines;
}

function deepDiff(oldVal, newVal) {
    if (oldVal === newVal) return undefined;
    if (oldVal == null && newVal == null) return undefined;

    const oldIsObj = oldVal && typeof oldVal === 'object';
    const newIsObj = newVal && typeof newVal === 'object';

    if (!oldIsObj || !newIsObj) {
        return { old: oldVal, new: newVal };
    }

    const result = {};
    const keys = new Set([...Object.keys(oldVal), ...Object.keys(newVal)]);

    for (const key of keys) {
        if (IGNORED_DIFF_KEYS.includes(key)) continue;
        const diff = deepDiff(oldVal[key], newVal[key]);
        if (diff !== undefined) {
            result[key] = diff;
        }
    }

    return Object.keys(result).length ? result : undefined;
}

function diffToLines(diff, ctx, path = []) {
    if (!diff) return [];
    const lines = [];

    for (const [key, value] of Object.entries(diff)) {
        if (value && typeof value === 'object' && 'old' in value && 'new' in value) {
            const fullPath = [...path.map(humaniseKey), humaniseKey(key)];

            // Whole object added or removed → unfold its fields through renderPayload.
            if (value.old == null && value.new != null && typeof value.new === 'object') {
                lines.push(...renderPayload(value.new, ctx, fullPath));
                continue;
            }
            if (value.old != null && value.new == null && typeof value.old === 'object') {
                lines.push(...renderPayload(value.old, ctx, fullPath));
                continue;
            }

            if (value.old == null) {
                lines.push({
                    path: fullPath,
                    value: <Scalar keyName={key} value={value.new} {...ctx} />,
                });
                continue;
            }

            if (value.new == null) {
                lines.push({
                    path: fullPath,
                    value: (
                        <>
                            was <Scalar keyName={key} value={value.old} {...ctx} />
                        </>
                    ),
                });
                continue;
            }

            lines.push({
                path: fullPath,
                value: (
                    <Box
                        component="span"
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                    >
                        <Scalar keyName={key} value={value.old} {...ctx} />
                        <span>→</span>
                        <Scalar keyName={key} value={value.new} {...ctx} />
                    </Box>
                ),
            });
            continue;
        }

        lines.push(...diffToLines(value, ctx, [...path, key]));
    }

    return lines;
}

// Groups consecutive change lines that share the same path prefix under a
// single header. Lines with a prefix are indented; top-level leaves stay flush.
function renderGroupedLines(lines) {
    const elements = [];
    let lastPrefix = null;
    lines.forEach((line, i) => {
        const prefix = line.path.slice(0, -1);
        const leafLabel = line.path[line.path.length - 1];
        const prefixKey = prefix.join(' › ');

        if (prefix.length === 0) {
            lastPrefix = null;
            elements.push(
                <Typography
                    key={`l-${i}`}
                    variant="body2"
                    color="textSecondary"
                    component="div"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        py: 0.25,
                        lineHeight: 1.4,
                    }}
                >
                    <Box component="span" sx={{ fontWeight: 500 }}>
                        {leafLabel}:
                    </Box>
                    <Box
                        component="span"
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                    >
                        {line.value}
                    </Box>
                </Typography>,
            );
            return;
        }

        if (prefixKey !== lastPrefix) {
            elements.push(
                <Typography
                    key={`h-${i}`}
                    variant="body2"
                    component="div"
                    sx={{ fontWeight: 500, mt: 0.75, color: 'text.primary' }}
                >
                    {prefixKey}
                </Typography>,
            );
            lastPrefix = prefixKey;
        }

        elements.push(
            <Typography
                key={`l-${i}`}
                variant="body2"
                color="textSecondary"
                component="div"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    py: 0.25,
                    pl: 4,
                    lineHeight: 1.4,
                }}
            >
                <Box component="span" sx={{ fontWeight: 500 }}>
                    {leafLabel}:
                </Box>
                <Box
                    component="span"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                >
                    {line.value}
                </Box>
            </Typography>,
        );
    });
    return elements;
}

const SNAPSHOT_EVENTS = new Set(['add_booking', 'edit_booking', 'booking_status_change']);

const EVENT_META = {
    add_booking: { color: 'success', labelKey: 'bookings.audit.added', fallback: 'Booking added' },
    edit_booking: {
        color: 'info',
        labelKey: 'bookings.audit.updated',
        fallback: 'Booking updated',
    },
    send_upgrade_offer: {
        color: 'info',
        labelKey: 'bookings.audit.upgradeOfferSent',
        fallback: 'Upgrade offer sent',
    },
    dismiss_upgrade_offer: {
        color: 'warning',
        labelKey: 'bookings.audit.upgradeOfferDismissed',
        fallback: 'Upgrade offer dismissed',
    },
    booking_status_change: {
        color: 'warning',
        labelKey: 'bookings.audit.statusChanged',
        fallback: 'Status changed',
    },
    booking_cancel_policy_override: {
        color: 'error',
        labelKey: 'bookings.audit.cancelPolicyOverride',
        fallback: 'Cancel policy overridden',
    },
    edit_rental_day: {
        color: 'info',
        labelKey: 'bookings.audit.rentalDayMoved',
        fallback: 'Rental day moved',
    },
    edit_booking_checkin: {
        color: 'info',
        labelKey: 'bookings.audit.checkinEdited',
        fallback: 'Check-in edited',
    },
    edit_booking_item_checkin_note: {
        color: 'info',
        labelKey: 'bookings.audit.checkinNoteEdited',
        fallback: 'Check-in note edited',
    },
    order_rollback: {
        color: 'warning',
        labelKey: 'bookings.audit.orderRollback',
        fallback: 'Order rolled back',
    },
    manually_trigger_rollback: {
        color: 'warning',
        labelKey: 'bookings.audit.manualRollback',
        fallback: 'Manual rollback triggered',
    },
    add_more_rollback_time: {
        color: 'default',
        labelKey: 'bookings.audit.moreRollbackTime',
        fallback: 'Rollback time extended',
    },
    manual_finish_transaction: {
        color: 'success',
        labelKey: 'bookings.audit.manualRefundFinished',
        fallback: 'Manual refund finished',
    },
    resend_email: {
        color: 'default',
        labelKey: 'bookings.audit.resendEmail',
        fallback: 'Email resent',
    },
    payment: {
        color: 'success',
        labelKey: 'bookings.audit.payment',
        fallback: 'Payment received',
    },
    refund: {
        color: 'warning',
        labelKey: 'bookings.audit.refund',
        fallback: 'Refund issued',
    },
    viator_booking_create: {
        color: 'success',
        labelKey: 'bookings.audit.viatorCreate',
        fallback: 'Viator booking created',
    },
    viator_booking_amendment: {
        color: 'info',
        labelKey: 'bookings.audit.viatorAmend',
        fallback: 'Viator booking amended',
    },
    viator_booking_cancellation: {
        color: 'error',
        labelKey: 'bookings.audit.viatorCancel',
        fallback: 'Viator booking cancelled',
    },
    re_source_booking: {
        color: 'warning',
        labelKey: 'bookings.audit.reSourced',
        fallback: 'Booking re-sourced',
    },
    attribute_widget_affiliate: {
        color: 'info',
        labelKey: 'bookings.audit.affiliateAttributed',
        fallback: 'Affiliate attribution',
    },
    attribute_integration_commission: {
        color: 'info',
        labelKey: 'bookings.audit.integrationCommission',
        fallback: 'Integration commission',
    },
    change_transaction_payment_type: {
        color: 'warning',
        labelKey: 'bookings.audit.paymentTypeChanged',
        fallback: 'Payment type changed',
    },
    edit_transaction_processing_fee: {
        color: 'warning',
        labelKey: 'bookings.audit.processingFeeEdited',
        fallback: 'Processing fee edited',
    },
    clear_timeslot_seat_assignments: {
        color: 'warning',
        labelKey: 'bookings.audit.seatUnassignedBulk',
        fallback: 'Seat un-assigned (bulk)',
    },
    seating_group_created: {
        color: 'success',
        labelKey: 'bookings.audit.seatingGroupCreated',
        fallback: 'Seating group linked',
    },
    seating_group_updated: {
        color: 'info',
        labelKey: 'bookings.audit.seatingGroupUpdated',
        fallback: 'Seating group updated',
    },
    seating_group_disbanded: {
        color: 'warning',
        labelKey: 'bookings.audit.seatingGroupDisbanded',
        fallback: 'Seating group disbanded',
    },
};

function parseLog(log, previousSnapshot, ctx) {
    let meta = EVENT_META[log.log_type] || {
        color: 'default',
        labelKey: '',
        fallback: log.log_type,
    };

    // Bulk-mode payment-type changes share the log_type with single-transaction
    // changes; only the log_data.bulk flag distinguishes them. Suffix the chip
    // so the per-booking history makes the difference obvious at a glance.
    if (log.log_type === 'change_transaction_payment_type' && log.log_data?.bulk) {
        meta = {
            ...meta,
            labelKey: 'bookings.audit.paymentTypeChangedBulk',
            fallback: 'Payment type changed (bulk)',
        };
    }

    if (log.log_type === 'booking_status_change') {
        const prevStatus = previousSnapshot?.log_data?.status;
        const nextStatus = log.log_data.status;
        const chipLabel = prevStatus ? `${prevStatus} → ${nextStatus}` : nextStatus;
        return {
            id: log.id,
            createdAt: log.created_at,
            user: log.user_name,
            meta: { ...meta, chipLabel },
            changes: [],
        };
    }

    if (SNAPSHOT_EVENTS.has(log.log_type)) {
        if (!previousSnapshot) {
            return {
                id: log.id,
                createdAt: log.created_at,
                user: log.user_name,
                meta,
                changes: [],
            };
        }
        return {
            id: log.id,
            createdAt: log.created_at,
            user: log.user_name,
            meta,
            changes: diffToLines(deepDiff(previousSnapshot.log_data, log.log_data), ctx),
        };
    }

    return {
        id: log.id,
        createdAt: log.created_at,
        user: log.user_name,
        meta,
        changes: renderPayload(log.log_data, ctx),
    };
}

const PREVIEW_LIMIT = 5;

function BookingAuditLogsBlock({ logs, loading, ticketTypes, ticketOptions, collapsible }) {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(() => new Set());
    const [showAll, setShowAll] = useState(false);

    const toggle = (id) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const rows = useMemo(() => {
        if (!logs || logs.length === 0) return [];
        const ctx = { ticketTypes, ticketOptions };
        const list = [];
        let lastSnapshot = null;
        for (const log of logs) {
            list.push(parseLog(log, lastSnapshot, ctx));
            if (SNAPSHOT_EVENTS.has(log.log_type)) {
                lastSnapshot = log;
            }
        }
        return list.reverse();
    }, [logs, ticketTypes, ticketOptions]);

    if (loading) {
        return (
            <Grid2 size={{ xs: 12 }}>
                <BlockWithTitle title={t('auditlogs.title', 'Audit log')} collapsible={collapsible}>
                    <Grid2 size={{ xs: 12 }} display="flex" justifyContent="center" p={2}>
                        <CircularProgress size={24} />
                    </Grid2>
                </BlockWithTitle>
            </Grid2>
        );
    }

    if (rows.length === 0) {
        return null;
    }

    return (
        <Grid2 size={{ xs: 12 }}>
            <BlockWithTitle
                title={t('auditlogs.title', 'Audit log')}
                collapsible={collapsible}
                headerRight={
                    <Typography variant="body2" color="text.secondary">
                        {t('auditlogs.eventCount', { count: rows.length })}
                    </Typography>
                }
            >
                <Grid2 size={{ xs: 12 }}>
                    <TableContainer>
                        <Table size="small" sx={{ minWidth: 650 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('auditlogs.columns.event', 'Event')}</TableCell>
                                    <TableCell>{t('auditlogs.columns.user', 'User')}</TableCell>
                                    <TableCell>{t('auditlogs.columns.when', 'When')}</TableCell>
                                    <TableCell padding="checkbox" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(showAll ? rows : rows.slice(0, PREVIEW_LIMIT)).map((row) => {
                                    const isOpen = expanded.has(row.id);
                                    const hasChanges = row.changes.length > 0;
                                    return (
                                        <React.Fragment key={row.id}>
                                            <TableRow hover>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        color={row.meta.color}
                                                        label={
                                                            row.meta.chipLabel ||
                                                            t(row.meta.labelKey, row.meta.fallback)
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {row.user && (
                                                        <Typography variant="body2">
                                                            {row.user}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ whiteSpace: 'nowrap' }}
                                                    >
                                                        {formatDateTimeInVenueTz(row.createdAt)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell padding="checkbox">
                                                    {hasChanges && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => toggle(row.id)}
                                                            aria-label="toggle details"
                                                        >
                                                            {isOpen ? (
                                                                <ExpandLessIcon fontSize="small" />
                                                            ) : (
                                                                <ExpandMoreIcon fontSize="small" />
                                                            )}
                                                        </IconButton>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                            {hasChanges && isOpen && (
                                                <TableRow>
                                                    <TableCell colSpan={4} sx={{ py: 1, pl: 4 }}>
                                                        {renderGroupedLines(row.changes)}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <ShowMoreToggle
                        total={rows.length}
                        limit={PREVIEW_LIMIT}
                        showAll={showAll}
                        onToggle={() => setShowAll((prev) => !prev)}
                    />
                </Grid2>
            </BlockWithTitle>
        </Grid2>
    );
}

export default BookingAuditLogsBlock;
